import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface ReportConfig {
  groupBy?: string;
  jobTypeId?: string;
  from?: string;
  to?: string;
  operator?: string;
  teamId?: string;
  classification?: string;
  sortBy?: string;
  sortDir?: string;
}

export interface SavedReport {
  id: string;
  name: string;
  description: string | null;
  config: ReportConfig;
  isShared: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  user: { name: string };
}

export function useSavedReports() {
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await api.get<{ reports: SavedReport[] }>("/analytics/saved-reports");
      setReports(result.reports);
    } catch {
      // Non-fatal
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const save = async (name: string, config: ReportConfig, description?: string, isShared?: boolean) => {
    const report = await api.post<SavedReport>("/analytics/saved-reports", {
      name,
      config,
      description,
      isShared,
    });
    setReports((prev) => [report, ...prev]);
    return report;
  };

  const update = async (id: string, data: Partial<{ name: string; config: ReportConfig; description: string; isShared: boolean }>) => {
    const updated = await api.patch<SavedReport>(`/analytics/saved-reports/${id}`, data);
    setReports((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));
    return updated;
  };

  const remove = async (id: string) => {
    await api.delete(`/analytics/saved-reports/${id}`);
    setReports((prev) => prev.filter((r) => r.id !== id));
  };

  return { reports, isLoading, save, update, remove, refetch: fetchReports };
}
